import React from "react";
import { BeatLoader } from "react-spinners";
import { useTranslation } from "react-i18next";
import { ProjectResponseDto } from "../common/ObjectTypes";
import { useNavigate } from "react-router-dom";
import tailwindClassnames from "../common/TailwindClassnames";
import Pagination from "../common/Pagination";

type ProjectsListProps = {
  projects: ProjectResponseDto[];
  totalCount: number;
  take: number;
  offset: number;
  loading: boolean;
  resultsLoaded: boolean;
  countries: any[];
  showOrganizationsColumn: boolean;
  hasProjects: boolean;
  onPageClick: Function;
};

/**
 * Projects table body for the list-page table card: column headers, rows,
 * loading/empty/no-access states, and the pagination footer. Expects to be
 * rendered inside a `tailwindClassnames.tableCard` container (below the
 * `SearchBarProjects` toolbar).
 */
const ProjectsList: React.FC<ProjectsListProps> = ({
  projects,
  totalCount,
  take,
  offset,
  loading,
  resultsLoaded,
  countries,
  showOrganizationsColumn,
  hasProjects,
  onPageClick,
}) => {
  const { t } = useTranslation();
  const getCountryName = (countryCode: any) => {
    const country = countries.find((country) => country.code === countryCode);
    return country ? country.name : "Country not found";
  };

  const navigate = useNavigate();

  const onSelectProject = (guid: string | undefined) => {
    if (!guid) return;
    navigate("/projects/" + guid + "/overview");
  };

  return (
    <div className="w-full">
      {loading ? (
        <div className="sweet-loading flex justify-center w-full py-8">
          <BeatLoader
            color={tailwindClassnames.skyBlueDark}
            loading={true}
            size={15}
            margin={5}
          />
        </div>
      ) : hasProjects ? (
        <>
          <table className="w-full">
            <thead>
              <tr>
                <th className={tailwindClassnames.tableCardHeadCell}>
                  {t("Project Name")}
                </th>
                {showOrganizationsColumn && (
                  <th className={tailwindClassnames.tableCardHeadCell}>
                    {t("Organization")}
                  </th>
                )}
                <th className={tailwindClassnames.tableCardHeadCell}>
                  {t("Country")}
                </th>
                <th className={tailwindClassnames.tableCardHeadCell}>
                  {t("Project Admin Email")}
                </th>
              </tr>
            </thead>
            <tbody>
              {resultsLoaded && projects.length === 0 && (
                <tr>
                  <td
                    colSpan={showOrganizationsColumn ? 4 : 3}
                    className="text-center py-3 text-primary-off-black dark:text-primary-off-white transition duration-300"
                  >
                    {t("No results found")}
                  </td>
                </tr>
              )}
              {resultsLoaded ? (
                projects.map((project) => {
                  const countryName = getCountryName(project.country_id);
                  return (
                    <tr
                      key={project.project_id}
                      onClick={() => onSelectProject(project.guid)}
                      className={tailwindClassnames.tableCardRow}
                    >
                      <td
                        className={tailwindClassnames.tableCardCellPrimary}
                        title={project.project_name ?? undefined}
                      >
                        {project.project_name}
                      </td>
                      {showOrganizationsColumn && (
                        <td
                          className={tailwindClassnames.tableCardCell}
                          title={project.org_name ?? undefined}
                        >
                          {project.org_name}
                        </td>
                      )}
                      <td
                        className={tailwindClassnames.tableCardCell}
                        title={countryName || undefined}
                      >
                        {countryName}
                      </td>
                      <td
                        className={tailwindClassnames.tableCardCell}
                        title={project.primary_contact_email ?? undefined}
                      >
                        {project.primary_contact_email}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={showOrganizationsColumn ? 4 : 3}
                    className="py-3 px-4 text-left text-sm text-primary-off-black dark:text-primary-off-white transition duration-300"
                  >
                    {t("Loading...")}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      ) : (
        <p className={`${tailwindClassnames.warningLabel} m-4 w-fit`}>
          {t("You do not have access to any projects")}
        </p>
      )}
      {/* No pagination footer in the no-access state — matches the
          Organizations list, which suppresses its pager there too. */}
      {!loading && hasProjects && (
        <Pagination
          showText={true}
          handlePageClick={onPageClick}
          pageCount={Math.ceil(totalCount / take)}
          resultStart={offset + 1}
          initialPage={Math.floor(offset / take)}
          resultEnd={offset + take < totalCount ? offset + take : totalCount}
          totalCount={totalCount}
          take={take}
          loading={false}
          mainclassname={tailwindClassnames.tableCardFooter}
        />
      )}
    </div>
  );
};

export default ProjectsList;
